import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/lib/permissions";
import { Loader } from "./Loader";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Shield, Users, Plus, Pencil, Trash2, Search, X } from "lucide-react";

// Types
interface Permission {
  id: string;
  name: string;
  description: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

interface PermissionManifest {
  permissions: Permission[];
  roles: Role[];
}

interface CreatePermissionData {
  name: string;
  description: string;
}

interface CreateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

/**
 * Enhanced Permission management component for admins
 */
export function PermissionManagement() {
  const { localUser } = useAuthStore();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();

  // State
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<
    Set<string>
  >(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatePermissionOpen, setIsCreatePermissionOpen] = useState(false);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [newPermission, setNewPermission] = useState<CreatePermissionData>({
    name: "",
    description: "",
  });
  const [newRole, setNewRole] = useState<CreateRoleData>({
    name: "",
    description: "",
    permissions: [],
  });

  // Check if user has permission to manage settings
  const canManagePermissions = hasPermission("manage_settings");

  // Queries
  const {
    data: permissionManifest,
    isLoading: manifestLoading,
    error: manifestError,
    refetch: refetchManifest,
  } = useQuery<PermissionManifest>({
    queryKey: ["permissionManifest"],
    queryFn: async () => {
      const response = await apiClient.get<PermissionManifest>(
        "/v1/permissions/manifest"
      );
      return response;
    },
    enabled: canManagePermissions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutations
  const createPermissionMutation = useMutation({
    mutationFn: async (data: CreatePermissionData) => {
      return await apiClient.post("/v1/permissions", { data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissionManifest"] });
      setIsCreatePermissionOpen(false);
      setNewPermission({ name: "", description: "" });
      toast.success("Permission created successfully", {
        position: "bottom-right",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create permission", {
        position: "bottom-right",
      });
    },
  });

  const createRoleMutation = useMutation({
    mutationFn: async (data: CreateRoleData) => {
      return await apiClient.post("/v1/roles", { data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissionManifest"] });
      setIsCreateRoleOpen(false);
      setNewRole({ name: "", description: "", permissions: [] });
      toast.success("Role created successfully", {
        position: "bottom-right",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create role", {
        position: "bottom-right",
      });
    },
  });

  const updateRolePermissionsMutation = useMutation({
    mutationFn: async ({
      roleId,
      permissionId,
      action,
    }: {
      roleId: string;
      permissionId: string;
      action: "add" | "remove";
    }) => {
      if (action === "add") {
        return await apiClient.post(
          `/v1/roles/${roleId}/permissions/${permissionId}`
        );
      } else {
        return await apiClient.delete(
          `/v1/roles/${roleId}/permissions/${permissionId}`
        );
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["permissionManifest"] });
      const role = permissionManifest?.roles.find(
        (r) => r.id === variables.roleId
      );
      toast.success(
        `Permission ${
          variables.action === "add" ? "added to" : "removed from"
        } role ${role?.name}`,
        { position: "bottom-right" }
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update role permissions", {
        position: "bottom-right",
      });
    },
  });

  const deletePermissionMutation = useMutation({
    mutationFn: async (permissionId: string) => {
      return await apiClient.delete(`/v1/permissions/${permissionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissionManifest"] });
      toast.success("Permission deleted successfully", {
        position: "bottom-right",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete permission", {
        position: "bottom-right",
      });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      return await apiClient.delete(`/v1/roles/${roleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissionManifest"] });
      setSelectedRole(null);
      setSelectedPermissionIds(new Set());
      toast.success("Role deleted successfully", {
        position: "bottom-right",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete role", {
        position: "bottom-right",
      });
    },
  });

  // Effects
  useEffect(() => {
    if (selectedRole && permissionManifest) {
      handleRoleSelect(selectedRole);
    }
  }, [permissionManifest]);

  // Handlers
  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);

    // Find role permissions
    const role = permissionManifest?.roles.find((r) => r.id === roleId);
    if (role) {
      // Find permission IDs from names
      const permissionIds = new Set<string>();
      role.permissions.forEach((permName) => {
        const perm = permissionManifest?.permissions.find(
          (p) => p.name === permName
        );
        if (perm) {
          permissionIds.add(perm.id);
        }
      });
      setSelectedPermissionIds(permissionIds);
    } else {
      setSelectedPermissionIds(new Set());
    }
  };

  const handleTogglePermission = async (
    permissionId: string,
    checked: boolean
  ) => {
    if (!selectedRole) return;

    updateRolePermissionsMutation.mutate({
      roleId: selectedRole,
      permissionId,
      action: checked ? "add" : "remove",
    });

    // Optimistically update UI
    setSelectedPermissionIds((prev) => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(permissionId);
      } else {
        updated.delete(permissionId);
      }
      return updated;
    });
  };

  const handleCreatePermission = () => {
    if (!newPermission.name.trim() || !newPermission.description.trim()) {
      toast.error("Please fill in all fields", { position: "bottom-right" });
      return;
    }
    createPermissionMutation.mutate(newPermission);
  };

  const handleCreateRole = () => {
    if (!newRole.name.trim() || !newRole.description.trim()) {
      toast.error("Please fill in all fields", { position: "bottom-right" });
      return;
    }
    createRoleMutation.mutate(newRole);
  };

  // Authorization check
  if (!canManagePermissions) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You don't have permission to manage system permissions and roles.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Loading state
  if (manifestLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader />
        </div>
      </div>
    );
  }

  // Error state
  if (manifestError || !permissionManifest) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">
              Error Loading Permissions
            </CardTitle>
            <CardDescription>
              {manifestError?.message || "Failed to load permission data"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => refetchManifest()} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter permissions based on search
  const filteredPermissions = permissionManifest.permissions.filter(
    (permission) =>
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Permission Management
          </h1>
          <p className="text-muted-foreground">
            Manage system permissions and user roles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {localUser?.role?.name || "Unknown Role"}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Role Permissions
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            System Permissions
          </TabsTrigger>
        </TabsList>

        {/* Role Management Tab */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Role Permissions</CardTitle>
                  <CardDescription>
                    Manage permissions for each role in the system
                  </CardDescription>
                </div>
                <Dialog
                  open={isCreateRoleOpen}
                  onOpenChange={setIsCreateRoleOpen}
                >
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Create Role
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Role</DialogTitle>
                      <DialogDescription>
                        Create a new role and assign permissions
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="role-name">Role Name</Label>
                        <Input
                          id="role-name"
                          value={newRole.name}
                          onChange={(e) =>
                            setNewRole({ ...newRole, name: e.target.value })
                          }
                          placeholder="Enter role name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="role-description">Description</Label>
                        <Input
                          id="role-description"
                          value={newRole.description}
                          onChange={(e) =>
                            setNewRole({
                              ...newRole,
                              description: e.target.value,
                            })
                          }
                          placeholder="Enter role description"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateRoleOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateRole}
                        disabled={createRoleMutation.isPending}
                      >
                        {createRoleMutation.isPending
                          ? "Creating..."
                          : "Create Role"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="role-select">Select Role</Label>
                  <Select
                    onValueChange={handleRoleSelect}
                    value={selectedRole || undefined}
                  >
                    <SelectTrigger id="role-select">
                      <SelectValue placeholder="Select a role to manage" />
                    </SelectTrigger>
                    <SelectContent>
                      {permissionManifest.roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{role.name}</span>
                            <Badge variant="outline" className="ml-2">
                              {role.permissions.length} permissions
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedRole && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Role</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this role? This action
                          cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            deleteRoleMutation.mutate(selectedRole)
                          }
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>

              {selectedRole && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      Permissions for{" "}
                      {
                        permissionManifest.roles.find(
                          (r) => r.id === selectedRole
                        )?.name
                      }
                    </h3>
                    <Badge variant="secondary">
                      {selectedPermissionIds.size} of{" "}
                      {permissionManifest.permissions.length} permissions
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {permissionManifest.permissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`perm-${permission.id}`}
                          checked={selectedPermissionIds.has(permission.id)}
                          onCheckedChange={(checked) =>
                            handleTogglePermission(
                              permission.id,
                              checked === true
                            )
                          }
                          disabled={updateRolePermissionsMutation.isPending}
                        />
                        <div className="grid gap-1.5 leading-none flex-1">
                          <Label
                            htmlFor={`perm-${permission.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {permission.name}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {permission.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Management Tab */}
        <TabsContent value="permissions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>System Permissions</CardTitle>
                  <CardDescription>
                    Manage individual permissions in the system
                  </CardDescription>
                </div>
                <Dialog
                  open={isCreatePermissionOpen}
                  onOpenChange={setIsCreatePermissionOpen}
                >
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Create Permission
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Permission</DialogTitle>
                      <DialogDescription>
                        Create a new permission for the system
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="permission-name">Permission Name</Label>
                        <Input
                          id="permission-name"
                          value={newPermission.name}
                          onChange={(e) =>
                            setNewPermission({
                              ...newPermission,
                              name: e.target.value,
                            })
                          }
                          placeholder="e.g., manage_users"
                        />
                      </div>
                      <div>
                        <Label htmlFor="permission-description">
                          Description
                        </Label>
                        <Input
                          id="permission-description"
                          value={newPermission.description}
                          onChange={(e) =>
                            setNewPermission({
                              ...newPermission,
                              description: e.target.value,
                            })
                          }
                          placeholder="Describe what this permission allows"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsCreatePermissionOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreatePermission}
                        disabled={createPermissionMutation.isPending}
                      >
                        {createPermissionMutation.isPending
                          ? "Creating..."
                          : "Create Permission"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search permissions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Permissions Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Permission</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Used in Roles</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPermissions.map((permission) => {
                      const rolesWithPermission =
                        permissionManifest.roles.filter((role) =>
                          role.permissions.includes(permission.name)
                        );

                      return (
                        <TableRow key={permission.id}>
                          <TableCell className="font-medium">
                            {permission.name}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {permission.description}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {rolesWithPermission.map((role) => (
                                <Badge
                                  key={role.id}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {role.name}
                                </Badge>
                              ))}
                              {rolesWithPermission.length === 0 && (
                                <span className="text-xs text-muted-foreground">
                                  Not used
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" disabled>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    disabled={rolesWithPermission.length > 0}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Delete Permission
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete the "
                                      {permission.name}" permission? This action
                                      cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() =>
                                        deletePermissionMutation.mutate(
                                          permission.id
                                        )
                                      }
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {filteredPermissions.length === 0 && (
                <div className="text-center py-8">
                  <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    No permissions found
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {searchTerm
                      ? "Try adjusting your search"
                      : "Get started by creating your first permission"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
